import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { LangToggle } from "./LangToggle";

const navItems = [
  { to: "/", key: "home" },
  { to: "/galeria", key: "gallery" },
  { to: "/o-mnie", key: "about" },
  { to: "/wystawy", key: "exhibitions" },
  { to: "/kontakt", key: "contact" },
];

export function Header() {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-bone/75 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="container-art flex items-center justify-between py-5 md:py-6">
        <Link
          to="/"
          data-testid="logo-link"
          className="group flex items-baseline gap-2"
          aria-label="Gonzaga — strona główna"
        >
          <span className="font-heading text-2xl md:text-[28px] font-light tracking-tight text-ink">
            Gonzaga
          </span>
          <span className="hidden md:inline overline">Atelier</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.22em] font-medium transition-colors ${
                  isActive ? "text-ink" : "text-stone hover:text-ink"
                }`
              }
            >
              {t.nav[item.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-6">
          <LangToggle />
          <button
            type="button"
            className="md:hidden p-1 text-ink"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        } bg-bone border-b border-black/5`}
      >
        <nav className="container-art py-8 flex flex-col gap-6">
          {navItems.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              data-testid={`mobile-nav-${item.key}`}
              className={({ isActive }) =>
                `font-heading text-3xl font-light tracking-tight ${
                  isActive ? "text-ink" : "text-stone"
                }`
              }
            >
              {t.nav[item.key]}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
